// Cognitive Skills Tracking and Scoring System
// Based on Native Brilliance Cognitive Assessment requirements

// Cognitive skill definitions
export const COGNITIVE_SKILLS = {
  M1: 'Inductive Reasoning',
  M2: 'Organization/Linear Thinking',
  M3: 'Agility',
  M4: 'Powers of Discrimination',
  F1: 'Awareness/Attention',
  F2a: 'Auditory/Verbal Memory',
  F2v: 'Visual Memory',
  F2e: 'Emotional Memory',
  F3: 'Deductive Reasoning',
  F4: 'Powers of Generalization',
  WM: 'Working Memory'
};

// Module to cognitive skills mapping
export const MODULE_SKILLS = {
  '1A': {
    skills: ['F1', 'F3', 'F4'],
    difficulty: 2, // Fixed difficulty for module 1A
    adaptive: false
  },
  '1B': {
    skills: ['F1', 'F2e', 'F3'],
    difficulty: 2,
    adaptive: false
  },
  '2': {
    skills: ['M1', 'M3', 'M2', 'M4', 'WM'],
    adaptive: true,
    startDifficulty: 5,
    instances: 5
  },
  '3': {
    skills: ['M3', 'M2', 'M4'],
    adaptive: true,
    startDifficulty: 5,
    instances: 5
  },
  '4A': {
    skills: ['M4', 'F2e', 'F4'],
    difficulty: 2,
    adaptive: false
  },
  '4B': {
    skills: ['F2a', 'F1'],  // CORRECTED: F2a (auditory/name recognition) and F1 (attention)
    difficulty: 2,
    adaptive: false
  },
  '4C': {
    skills: ['M1', 'F2v', 'M4'],
    difficulty: 2,
    adaptive: false
  },
  '5': {
    skills: ['F3', 'F2e', 'M2'],
    difficulty: 1,
    adaptive: false
  },
  '6': {
    skills: ['M1', 'F2a', 'M3'],
    adaptive: true,
    startDifficulty: 5,
    instances: 5
  },
  '7': {
    skills: ['F3', 'F4', 'M2'],
    adaptive: true,
    startDifficulty: 5,
    instances: 5
  },
  '8': {
    skills: ['WM', 'F2v'],
    difficulty: 2,
    adaptive: false
  }
};

// Class to manage cognitive skill scores and adaptive difficulty
export class CognitiveSkillsTracker {
  constructor() {
    this.skillScores = {};
    this.moduleHistory = {};
    this.currentModuleState = {};
    
    // Initialize skill scores
    Object.keys(COGNITIVE_SKILLS).forEach(skill => {
      this.skillScores[skill] = 0;
    });
  }

  // Start a new module
  startModule(moduleId) {
    const config = MODULE_SKILLS[moduleId];
    
    if (!this.moduleHistory[moduleId]) {
      this.moduleHistory[moduleId] = {
        attempts: [],
        currentInstance: 0,
        totalInstances: config.instances || 1,
        questionsAnswered: 0,
        correctAnswers: 0
      };
    }
    
    if (config.adaptive) {
      // Use actual available difficulties for each module
      let availableDifficulties = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
      
      // Module-specific difficulty pools based on actual instance data
      if (moduleId === '2') {
        availableDifficulties = [1.0, 2.0, 4.0, 5.0, 6.0, 7.0, 8.0, 9.0, 9.5, 10.0];
      }
      
      this.currentModuleState[moduleId] = {
        currentDifficulty: config.startDifficulty,
        instancesCompleted: 0,
        difficultyHistory: [],
        availableDifficulties: [...availableDifficulties], // Clone the array
        usedInstanceIds: [] // Track actual instance IDs used
      };
    } else {
      // Non-adaptive modules have fixed difficulty
      this.currentModuleState[moduleId] = {
        currentDifficulty: config.difficulty || 2,
        questionsAnswered: 0,
        isNonAdaptive: true
      };
    }
    
    return this.currentModuleState[moduleId];
  }

  // Record an answer and calculate next difficulty
  recordAnswer(moduleId, isCorrect, currentDifficulty = null, instanceId = null, customPoints = null) {
    const config = MODULE_SKILLS[moduleId];
    
    // Calculate points
    const difficulty = currentDifficulty || config.difficulty || 2;
    const points = customPoints !== null ? customPoints : (isCorrect ? difficulty : 0);
    
    // Update skill scores - add points whenever earned (not just when fully correct)
    // This is important for Module 8 which uses partial credit scoring
    if (points > 0 && config.skills) {
      config.skills.forEach(skill => {
        this.skillScores[skill] += points;
      });
    }
    
    // Record attempt
    if (!this.moduleHistory[moduleId]) {
      this.moduleHistory[moduleId] = { 
        attempts: [],
        questionsAnswered: 0,
        correctAnswers: 0 
      };
    }
    
    this.moduleHistory[moduleId].attempts.push({
      difficulty,
      isCorrect,
      points,
      instanceId,
      timestamp: new Date().toISOString()
    });
    
    // Update question counts
    this.moduleHistory[moduleId].questionsAnswered++;
    if (isCorrect) {
      this.moduleHistory[moduleId].correctAnswers++;
    }
    
    // Handle non-adaptive modules
    if (!config.adaptive && this.currentModuleState[moduleId]) {
      const state = this.currentModuleState[moduleId];
      state.questionsAnswered++;
      
      return {
        moduleId,
        isCorrect,
        points,
        questionsAnswered: state.questionsAnswered,
        isNonAdaptive: true
      };
    }
    
    // Handle adaptive modules
    if (config.adaptive && this.currentModuleState[moduleId]) {
      const state = this.currentModuleState[moduleId];
      state.difficultyHistory.push(difficulty);
      state.instancesCompleted++;
      
      // Track used instance ID
      if (instanceId !== null) {
        state.usedInstanceIds.push(instanceId);
      }
      
      // Calculate next difficulty
      if (state.instancesCompleted < config.instances) {
        const nextDifficulty = this.calculateNextDifficulty(
          difficulty,
          isCorrect,
          state.availableDifficulties,
          state.usedInstanceIds
        );
        
        state.currentDifficulty = nextDifficulty;
        
        // Remove used difficulty from available pool
        const index = state.availableDifficulties.indexOf(difficulty);
        if (index > -1) {
          state.availableDifficulties.splice(index, 1);
        }
        
        return {
          nextDifficulty,
          instancesRemaining: config.instances - state.instancesCompleted,
          isAdaptive: true
        };
      }
    }
    
    return null;
  }

  // Calculate next difficulty based on performance
  calculateNextDifficulty(currentDifficulty, isCorrect, availableDifficulties, usedInstanceIds) {
    // Filter out unused difficulties (availableDifficulties is already being filtered in recordAnswer)
    const unused = [...availableDifficulties];
    
    if (unused.length === 0) {
      // If no difficulties left, return null to end module
      console.log('⚠️ No more difficulties available, ending module');
      return null;
    }
    
    console.log(`🎯 Calculating next difficulty: current=${currentDifficulty}, isCorrect=${isCorrect}, unused=[${unused.join(', ')}]`);
    
    if (isCorrect) {
      // Find next higher difficulty that we haven't used
      const higher = unused.filter(d => d > currentDifficulty).sort((a, b) => a - b);
      if (higher.length > 0) {
        console.log(`✅ Correct answer - selecting higher difficulty: ${higher[0]}`);
        return higher[0];
      }
      // If no higher available, get closest lower
      const lower = unused.filter(d => d < currentDifficulty).sort((a, b) => b - a);
      const selected = lower[0] || unused[0];
      console.log(`✅ Correct answer - no higher available, selecting: ${selected}`);
      return selected;
    } else {
      // Find next lower difficulty that we haven't used
      const lower = unused.filter(d => d < currentDifficulty).sort((a, b) => b - a);
      if (lower.length > 0) {
        console.log(`❌ Incorrect answer - selecting lower difficulty: ${lower[0]}`);
        return lower[0];
      }
      // If no lower available, get closest higher
      const higher = unused.filter(d => d > currentDifficulty).sort((a, b) => a - b);
      const selected = higher[0] || unused[0];
      console.log(`❌ Incorrect answer - no lower available, selecting: ${selected}`);
      return selected;
    }
  }

  // Calculate maximum possible scores for each skill
  getMaxPossibleScores() {
    const maxScores = {};
    
    // Initialize all skills to 0
    Object.keys(COGNITIVE_SKILLS).forEach(skill => {
      maxScores[skill] = 0;
    });
    
    // Module-specific max points (questions × difficulty)
    const moduleMaxPoints = {
      '1A': 9 * 2,   // 9 questions × 2 points = 18 points
      '1B': 9 * 2,   // 9 questions × 2 points = 18 points
      '2': 35,       // Adaptive: 5+6+7+8+9 = 35 points max (start at 5)
      '3': 35,       // Adaptive: 5+6+7+8+9 = 35 points max (start at 5)
      '4A': 12 * 2,  // 12 questions × 2 points = 24 points
      '4B': 12 * 2,  // 12 questions × 2 points = 24 points
      '4C': 12 * 2,  // 12 questions × 2 points = 24 points
      '5': 20 * 1,   // 20 Stroop test instances × 1 point = 20 points
      '6': 35,       // Adaptive: 5+6+7+8+9 = 35 points max (start at 5)
      '7': 35,       // Adaptive: 5+6+7+8+9 = 35 points max (start at 5)
      '8': 30        // Phone number: Capped contribution to F2v at 30 points
    };
    
    // Add max scores from each module to appropriate skills
    Object.entries(MODULE_SKILLS).forEach(([moduleId, config]) => {
      const modulePoints = moduleMaxPoints[moduleId] || 0;
      config.skills.forEach(skill => {
        maxScores[skill] += modulePoints;
      });
    });
    
    return maxScores;
  }

  // Get percentile rankings for each skill
  calculatePercentiles() {
    const percentiles = {};
    const maxScores = this.getMaxPossibleScores();
    
    Object.entries(this.skillScores).forEach(([skill, score]) => {
      const maxPossibleScore = maxScores[skill];
      const rawPercentile = maxPossibleScore > 0 ? (score / maxPossibleScore) * 100 : 0;
      
      // Compare against dummy database and apply normal curve
      percentiles[skill] = Math.min(99, Math.round(
        this.calculatePercentileWithNormData(rawPercentile, skill)
      ));
    });
    
    return percentiles;
  }

  // Calculate percentile using dummy normative data
  calculatePercentileWithNormData(rawPercentile, skill) {
    // Dummy database simulation - normal distribution around 50th percentile
    // In production, this would query actual user data
    const dummyScores = this.generateDummyScores(skill);
    
    // Find percentile rank in dummy population
    const belowCount = dummyScores.filter(score => score < rawPercentile).length;
    const percentileRank = (belowCount / dummyScores.length) * 100;
    
    return Math.max(1, Math.min(99, percentileRank));
  }

  // Generate dummy normative scores for initial percentile calculations
  generateDummyScores(skill) {
    // Create 1000 dummy scores with normal distribution (mean=50, std=15)
    const scores = [];
    for (let i = 0; i < 1000; i++) {
      // Box-Muller transformation for normal distribution
      const u1 = Math.random();
      const u2 = Math.random();
      const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
      const score = Math.max(0, Math.min(100, 50 + 15 * z));
      scores.push(score);
    }
    return scores.sort((a, b) => a - b);
  }

  // Apply a normal curve to percentile calculations
  applyCurve(rawPercentile) {
    // Simple S-curve transformation
    const x = (rawPercentile - 50) / 20;
    const curved = 50 + 50 * Math.tanh(x);
    return Math.max(1, Math.min(99, curved));
  }

  // Get current state for persistence
  getState() {
    return {
      skillScores: this.skillScores,
      moduleHistory: this.moduleHistory,
      currentModuleState: this.currentModuleState,
      maxPossibleScores: this.getMaxPossibleScores()
    };
  }

  // Restore from saved state
  setState(state) {
    if (state.skillScores) this.skillScores = state.skillScores;
    if (state.moduleHistory) this.moduleHistory = state.moduleHistory;
    if (state.currentModuleState) this.currentModuleState = state.currentModuleState;
  }

  // Reset tracker
  reset() {
    Object.keys(COGNITIVE_SKILLS).forEach(skill => {
      this.skillScores[skill] = 0;
    });
    this.moduleHistory = {};
    this.currentModuleState = {};
  }
}

// Singleton instance
let trackerInstance = null;

export const getCognitiveTracker = () => {
  if (!trackerInstance) {
    trackerInstance = new CognitiveSkillsTracker();
  }
  return trackerInstance;
};

export default getCognitiveTracker;